import { ComponentType } from "@/types";
import { chartTitleEvents, type ChartTitleEvent } from "@/hooks/useAutoChartTitle";

// Data point interfaces
export interface ChartDataPoint {
  [key: string]: any;
  name?: string | number;
  value?: number;
  label?: string;
  x?: number;
  y?: number;
  category?: string;
}

export interface DataOperation {
  type: "add" | "remove" | "update" | "replace" | "clear" | "import" | "generate";
  target?: string | number; // Index, label, or identifier
  data?: ChartDataPoint | ChartDataPoint[];
  field?: string;
  value?: any;
}

export interface DataTransformation {
  type: "sort" | "filter" | "aggregate" | "normalize" | "interpolate";
  field?: string;
  direction?: "asc" | "desc";
  condition?: (point: ChartDataPoint) => boolean;
  method?: "sum" | "average" | "min" | "max" | "count";
}

const isChartDataPoint = (value: unknown): value is ChartDataPoint =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// Callback types for data operations
export type DataOperationCallback = (
  chartId: string,
  newData: ChartDataPoint[],
  operation: DataOperation,
  chartType?: 'line' | 'bar' | 'area'
) => void;

// Chart data manager class
export class ChartDataManager {
  private static instance: ChartDataManager;
  private dataOperationCallbacks: DataOperationCallback[] = [];

  static getInstance(): ChartDataManager {
    if (!ChartDataManager.instance) {
      ChartDataManager.instance = new ChartDataManager();
    }
    return ChartDataManager.instance;
  }

  /**
   * Register a callback for data operations
   * @returns Unsubscribe function
   */
  onDataOperation(callback: DataOperationCallback): () => void {
    this.dataOperationCallbacks.push(callback);
    return () => {
      this.dataOperationCallbacks = this.dataOperationCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Emit data operation event to all registered callbacks
   */
  private emitDataOperation(
    chartId: string,
    newData: ChartDataPoint[],
    operation: DataOperation,
    chartType?: 'line' | 'bar' | 'area'
  ): void {
    // Emit to registered callbacks
    this.dataOperationCallbacks.forEach(callback => {
      try {
        callback(chartId, newData, operation, chartType);
      } catch (error) {
        console.error("[ChartDataManager] Callback error:", error);
      }
    });

    // Emit title generation event if chart type is provided
    if (chartType && newData.length > 0) {
      const fieldNames = this.extractFieldNames(newData);
      const event: ChartTitleEvent = {
        chartId,
        fieldNames,
        chartType,
        sampleData: newData.slice(0, 3)
      };
      chartTitleEvents.emit(event);
    }
  }

  /**
   * Extract unique field names from data
   */
  private extractFieldNames(data: ChartDataPoint[]): string[] {
    const fieldSet = new Set<string>();
    data.forEach(point => {
      Object.keys(point).forEach(key => fieldSet.add(key));
    });
    return Array.from(fieldSet);
  }

  /**
   * Parse data-related commands from natural language
   */
  parseDataCommand(command: string, currentData: ChartDataPoint[] = []): DataOperation | null {
    const lowerCommand = command.toLowerCase();

    // Add data point
    if (this.isAddCommand(lowerCommand)) {
      return this.parseAddCommand(command);
    }

    // Remove data point
    if (this.isRemoveCommand(lowerCommand)) {
      return this.parseRemoveCommand(command, currentData);
    }

    // Update data point
    if (this.isUpdateCommand(lowerCommand)) {
      return this.parseUpdateCommand(command, currentData);
    }

    // Clear all data
    if (lowerCommand.includes("clear") && lowerCommand.includes("data")) {
      return { type: "clear" };
    }

    // Import data
    if (lowerCommand.includes("import") || lowerCommand.includes("paste")) {
      return { type: "import" };
    }

    // Generate sample data
    if (lowerCommand.includes("generate") || lowerCommand.includes("sample data")) {
      return this.parseGenerateCommand(command);
    }

    return null;
  }

  private isAddCommand(command: string): boolean {
    const addPatterns = [
      "add",
      "insert",
      "create",
      "new data",
      "add point",
      "add data",
    ];
    return addPatterns.some(pattern => command.includes(pattern));
  }

  private isRemoveCommand(command: string): boolean {
    // Check if it's NOT an axis/axes command first
    if (command.includes("axis") || command.includes("axes")) {
      return false;
    }

    const removePatterns = [
      "remove data",
      "remove point",
      "delete data",
      "delete point",
      "drop data",
      "drop point",
      "clear point",
      "eliminate data",
      "remove last",
      "remove first",
    ];
    // Check for more specific patterns first
    if (removePatterns.some(pattern => command.includes(pattern))) {
      return true;
    }

    // Then check for generic remove with data-related context
    if (command.includes("remove") || command.includes("delete")) {
      // Only consider it a data command if it mentions data-related terms
      const dataTerms = ["data", "point", "value", "entry", "item", "row", "last", "first", "all"];
      return dataTerms.some(term => command.includes(term));
    }

    return false;
  }

  private isUpdateCommand(command: string): boolean {
    const updatePatterns = [
      "update",
      "change",
      "modify",
      "set",
      "edit",
      "adjust",
    ];
    return updatePatterns.some(pattern => command.includes(pattern));
  }

  private parseAddCommand(command: string): DataOperation {
    const dataPoint: ChartDataPoint = {};

    // Parse month names
    const months = [
      "january", "february", "march", "april", "may", "june",
      "july", "august", "september", "october", "november", "december"
    ];
    const monthMatch = months.find(month => command.toLowerCase().includes(month));
    if (monthMatch) {
      dataPoint.name = monthMatch.charAt(0).toUpperCase() + monthMatch.slice(1, 3);
    }

    // Parse quarter patterns
    const quarterMatch = command.match(/Q([1-4])|quarter\s*([1-4])/i);
    if (quarterMatch) {
      const quarter = quarterMatch[1] || quarterMatch[2];
      dataPoint.name = `Q${quarter}`;
    }

    // Parse year patterns
    const yearMatch = command.match(/\b(19\d{2}|20\d{2})\b/);
    if (yearMatch) {
      dataPoint.name = yearMatch[1];
    }

    // Parse generic labels
    if (!dataPoint.name) {
      const labelMatch = command.match(/(?:label|name|category)[:\s]+["']([^"']+)["']/i) ||
                         command.match(/["']([^"']+)["']/);
      if (labelMatch) {
        dataPoint.name = labelMatch[1];
      }
    }

    // Parse numeric value
    const valueMatch = command.match(/(?:value|amount|number|worth|sales|revenue|cost|price)?[:\s]*(\d+\.?\d*)/i);
    if (valueMatch) {
      dataPoint.value = parseFloat(valueMatch[1]);
    }

    // Parse percentage
    const percentMatch = command.match(/(\d+\.?\d*)\s*%/);
    if (percentMatch) {
      dataPoint.value = parseFloat(percentMatch[1]);
    }

    // Parse with/of patterns (e.g., "March with 500", "sales of 1000")
    const withMatch = command.match(/(\w+)\s+(?:with|of|at)\s+(\d+\.?\d*)/i);
    if (withMatch) {
      dataPoint.name = dataPoint.name || withMatch[1];
      dataPoint.value = parseFloat(withMatch[2]);
    }

    // Default values if not found
    if (!dataPoint.name) {
      dataPoint.name = `Item ${new Date().getTime()}`;
    }
    if (dataPoint.value === undefined) {
      dataPoint.value = Math.floor(Math.random() * 100) + 1;
    }

    return {
      type: "add",
      data: dataPoint,
    };
  }

  private parseRemoveCommand(command: string, currentData: ChartDataPoint[]): DataOperation {
    // Remove by index
    const indexMatch = command.match(/(?:index|position|item)\s*#?(\d+)/i);
    if (indexMatch) {
      return {
        type: "remove",
        target: parseInt(indexMatch[1]),
      };
    }

    // Remove last/first
    if (command.includes("last")) {
      return {
        type: "remove",
        target: currentData.length - 1,
      };
    }
    if (command.includes("first")) {
      return {
        type: "remove",
        target: 0,
      };
    }

    // Remove by label/name
    const labelMatch = command.match(/remove\s+["']?([^"']+)["']?/i);
    if (labelMatch) {
      const label = labelMatch[1].trim();
      const index = currentData.findIndex(d =>
        d.name?.toString().toLowerCase() === label.toLowerCase() ||
        d.label?.toLowerCase() === label.toLowerCase()
      );
      if (index >= 0) {
        return {
          type: "remove",
          target: index,
        };
      }
    }

    return {
      type: "remove",
      target: 0, // Default to first item
    };
  }

  private parseUpdateCommand(command: string, currentData: ChartDataPoint[]): DataOperation {
    let target: string | number = 0;
    let field = "value";
    let value: any;

    // Parse target (what to update)
    const targetMatch = command.match(/update\s+["']?([^"']+)["']?\s+to/i) ||
                       command.match(/change\s+["']?([^"']+)["']?\s+to/i) ||
                       command.match(/set\s+["']?([^"']+)["']?\s+to/i);
    if (targetMatch) {
      const targetStr = targetMatch[1].trim();
      // Check if it's an index
      const indexMatch = targetStr.match(/^#?(\d+)$/);
      if (indexMatch) {
        target = parseInt(indexMatch[1]);
      } else {
        // Find by label
        const index = currentData.findIndex(d =>
          d.name?.toString().toLowerCase() === targetStr.toLowerCase() ||
          d.label?.toLowerCase() === targetStr.toLowerCase()
        );
        target = index >= 0 ? index : targetStr;
      }
    }

    // Parse new value
    const valueMatch = command.match(/to\s+(\d+\.?\d*)/i);
    if (valueMatch) {
      value = parseFloat(valueMatch[1]);
    } else {
      const stringMatch = command.match(/to\s+["']([^"']+)["']/i);
      if (stringMatch) {
        value = stringMatch[1];
      }
    }

    // Parse field to update
    if (command.includes("name") || command.includes("label")) {
      field = "name";
    } else if (command.includes("value") || command.includes("amount")) {
      field = "value";
    }

    return {
      type: "update",
      target,
      field,
      value,
    };
  }

  private parseGenerateCommand(command: string): DataOperation {
    const countMatch = command.match(/(\d+)\s+(?:points?|items?|samples?)/i);
    const count = countMatch ? parseInt(countMatch[1]) : 5;

    // Generate sample data based on context
    const data: ChartDataPoint[] = [];

    if (command.includes("month")) {
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      for (let i = 0; i < Math.min(count, 12); i++) {
        data.push({
          name: months[i],
          value: Math.floor(Math.random() * 1000) + 100,
        });
      }
    } else if (command.includes("quarter")) {
      for (let i = 1; i <= Math.min(count, 4); i++) {
        data.push({
          name: `Q${i}`,
          value: Math.floor(Math.random() * 5000) + 1000,
        });
      }
    } else if (command.includes("year")) {
      const currentYear = new Date().getFullYear();
      for (let i = 0; i < count; i++) {
        data.push({
          name: (currentYear - count + i + 1).toString(),
          value: Math.floor(Math.random() * 10000) + 5000,
        });
      }
    } else {
      // Generic data
      for (let i = 0; i < count; i++) {
        data.push({
          name: `Item ${i + 1}`,
          value: Math.floor(Math.random() * 100) + 10,
        });
      }
    }

    return {
      type: "generate",
      data,
    };
  }

  /**
   * Apply data operation to current data
   */
  applyDataOperation(currentData: ChartDataPoint[], operation: DataOperation): ChartDataPoint[] {
    const data = [...currentData];

    switch (operation.type) {
      case "add":
        if (operation.data) {
          if (Array.isArray(operation.data)) {
            return [...data, ...operation.data];
          } else {
            return [...data, operation.data];
          }
        }
        break;

      case "remove":
        if (typeof operation.target === "number") {
          data.splice(operation.target, 1);
          return data;
        } else if (typeof operation.target === "string") {
          return data.filter(d =>
            d.name?.toString() !== operation.target &&
            d.label !== operation.target
          );
        }
        break;

      case "update":
        if (typeof operation.target === "number" && data[operation.target]) {
          if (operation.field) {
            // Create a new object to avoid mutating read-only data
            data[operation.target] = {
              ...data[operation.target],
              [operation.field]: operation.value,
            };
          }
        } else if (typeof operation.target === "string") {
          const index = data.findIndex(d =>
            d.name?.toString() === operation.target ||
            d.label === operation.target
          );
          if (index >= 0 && operation.field) {
            // Create a new object to avoid mutating read-only data
            data[index] = {
              ...data[index],
              [operation.field]: operation.value,
            };
          }
        }
        return data;

      case "replace":
        if (operation.data && Array.isArray(operation.data)) {
          return operation.data;
        }
        break;

      case "clear":
        return [];

      case "generate":
        if (operation.data && Array.isArray(operation.data)) {
          return operation.data;
        }
        break;
    }

    return data;
  }

  /**
   * Apply data operation and emit event for title generation
   * This is the preferred method when you want automatic title updates
   */
  applyDataOperationWithTitleUpdate(
    currentData: ChartDataPoint[],
    operation: DataOperation,
    chartId: string,
    chartType?: 'line' | 'bar' | 'area'
  ): ChartDataPoint[] {
    const newData = this.applyDataOperation(currentData, operation);

    // Emit the data operation event (will trigger title generation)
    this.emitDataOperation(chartId, newData, operation, chartType);

    return newData;
  }

  /**
   * Parse CSV/JSON data from clipboard or text
   */
  parseImportedData(text: string): ChartDataPoint[] | null {
    try {
      // Try JSON first
      if (text.trim().startsWith("[") || text.trim().startsWith("{")) {
        const parsed: unknown = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parsed.filter(isChartDataPoint);
        }
        return isChartDataPoint(parsed) ? [parsed] : null;
      }

      // Try CSV
      const lines = text.trim().split("\n");
      if (lines.length > 0) {
        const headers = lines[0].split(/[,\t]/).map(h => h.trim());
        const data: ChartDataPoint[] = [];

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(/[,\t]/).map(v => v.trim());
          const point: ChartDataPoint = {};

          headers.forEach((header, index) => {
            const value = values[index];
            // Try to parse as number
            const numValue = parseFloat(value);
            point[header] = isNaN(numValue) ? value : numValue;
          });

          data.push(point);
        }

        return data;
      }
    } catch (error) {
      console.error("Failed to parse imported data:", error);
    }

    return null;
  }

  /**
   * Transform data for different chart types
   */
  transformDataForChartType(data: ChartDataPoint[], fromType: ComponentType, toType: ComponentType): ChartDataPoint[] {
    // Most chart types use the same data structure
    // Pie charts might need aggregation if there's series data
    if (toType === ComponentType.PIE_CHART && data.length > 0) {
      // If data has multiple value columns, sum them
      const firstPoint = data[0];
      const valueKeys = Object.keys(firstPoint).filter(key =>
        key !== "name" && key !== "label" && typeof firstPoint[key] === "number"
      );

      if (valueKeys.length > 1) {
        // Aggregate multiple values into single value
        return data.map(point => ({
          name: point.name || point.label,
          value: valueKeys.reduce((sum, key) => sum + (point[key] as number || 0), 0),
        }));
      }
    }

    return data;
  }

  /**
   * Generate agricultural-specific sample data
   */
  generateAgriculturalData(dataType: string, count: number = 12): ChartDataPoint[] {
    const data: ChartDataPoint[] = [];

    switch (dataType.toLowerCase()) {
      case "crop yield":
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        for (let i = 0; i < Math.min(count, 12); i++) {
          data.push({
            name: months[i],
            value: Math.floor(Math.random() * 50) + 30, // 30-80 tons
            rainfall: Math.floor(Math.random() * 100) + 50, // mm
          });
        }
        break;

      case "soil moisture":
        for (let i = 0; i < count; i++) {
          data.push({
            name: `Day ${i + 1}`,
            value: Math.floor(Math.random() * 40) + 40, // 40-80%
          });
        }
        break;

      case "irrigation":
        const fields = ["Field A", "Field B", "Field C", "Field D"];
        for (let i = 0; i < Math.min(count, fields.length); i++) {
          data.push({
            name: fields[i],
            value: Math.floor(Math.random() * 1000) + 500, // liters
          });
        }
        break;

      case "livestock":
        const types = ["Cattle", "Sheep", "Goats", "Chickens"];
        for (let i = 0; i < Math.min(count, types.length); i++) {
          data.push({
            name: types[i],
            value: Math.floor(Math.random() * 100) + 20,
          });
        }
        break;

      default:
        // Generic agricultural data
        for (let i = 0; i < count; i++) {
          data.push({
            name: `Sample ${i + 1}`,
            value: Math.floor(Math.random() * 100) + 10,
          });
        }
    }

    return data;
  }

  /**
   * Validate data for chart type
   */
  validateDataForChartType(data: ChartDataPoint[], chartType: ComponentType): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!Array.isArray(data)) {
      errors.push("Data must be an array");
      return { valid: false, errors };
    }

    if (data.length === 0) {
      errors.push("Data array is empty");
      return { valid: false, errors };
    }

    // Check required fields based on chart type
    switch (chartType) {
      case ComponentType.LINE_CHART:
      case ComponentType.BAR_CHART:
      case ComponentType.AREA_CHART:
        data.forEach((point, index) => {
          if (!point.name && !point.label && !point.x) {
            errors.push(`Data point ${index} is missing a name/label/x field`);
          }
          if (point.value === undefined && point.y === undefined) {
            errors.push(`Data point ${index} is missing a value/y field`);
          }
        });
        break;

      case ComponentType.PIE_CHART:
        data.forEach((point, index) => {
          if (!point.name && !point.label) {
            errors.push(`Data point ${index} is missing a name/label field`);
          }
          if (point.value === undefined) {
            errors.push(`Data point ${index} is missing a value field`);
          }
        });
        break;

      case ComponentType.SCATTER_CHART:
        data.forEach((point, index) => {
          if (point.x === undefined) {
            errors.push(`Data point ${index} is missing an x field`);
          }
          if (point.y === undefined) {
            errors.push(`Data point ${index} is missing a y field`);
          }
        });
        break;
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

export const chartDataManager = ChartDataManager.getInstance();
